import React, { useState, useEffect } from 'react';
import { database, ref, get, update } from '../../firebase';
import { Award, Users, DollarSign, Gift, CheckCircle, Target, Calendar, Clock, Trophy, Lock, AlertCircle, Battery, Layers } from 'lucide-react';

// Dialog Component
const Dialog = ({ isOpen, onClose, title, message, type = 'info' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl p-6 max-w-sm w-full shadow-2xl">
        <div className="text-center">
          <div className="mb-4">
            {type === 'success' ? (
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            ) : type === 'error' ? (
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            ) : (
              <Award className="w-12 h-12 text-blue-500 mx-auto" />
            )}
          </div>
          
          <h3 className="text-lg font-bold text-foreground mb-2">{title}</h3>
          <p className="text-muted-foreground mb-6">{message}</p>
          
          <button
            onClick={onClose}
            className="btn-electric px-6 py-2 rounded-lg w-full"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const LeaderRewards = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [teamPackages, setTeamPackages] = useState([]);
  const [allRewards, setAllRewards] = useState([]);
  const [claimedRewardIds, setClaimedRewardIds] = useState(new Set());
  const [totalRewardsEarned, setTotalRewardsEarned] = useState(0);
  const [timeFrame, setTimeFrame] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [claimingRewardId, setClaimingRewardId] = useState(null);
  const [showLevelBreakdown, setShowLevelBreakdown] = useState(false);
  const [levelSales, setLevelSales] = useState([]);
  
  // User package status state
  const [userPackageStatus, setUserPackageStatus] = useState('inactive');
  const [userActivePackage, setUserActivePackage] = useState(null);
  
  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Get user info
  const userId = localStorage.getItem('userId') || '';
  const userName = localStorage.getItem('userName') || '';
  const userSponsorId = localStorage.getItem('userSponsorId') || userId;
  const userDbKey = localStorage.getItem('dbKey') || '';
  const registrationDate = localStorage.getItem('registrationDate') || '';

  useEffect(() => {
    fetchAllData();
  }, []);

  const showDialog = (title, message, type = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog({
      isOpen: false,
      title: '',
      message: '',
      type: 'info'
    });
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchUserData(),
        fetchAllRewards(),
        fetchClaimedRewards(),
        checkUserPackageStatus()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showDialog('Error', 'Failed to load rewards data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      if (!userDbKey || !registrationDate) {
        showDialog('Session Error', 'Please login again', 'error');
        return;
      }

      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const user = snapshot.val();
        setUserData(user);
        await calculateTeamSales(userSponsorId);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      showDialog('Error', 'Failed to load user data', 'error');
    }
  };

  // Check if user has active packages
  const hasActivePackages = (user) => {
    if (!user) return false;
    
    const packages = user.packages || [];
    
    if (Array.isArray(packages)) {
      return packages.some(pkg => {
        const status = pkg.status || pkg.packageStatus || pkg.state;
        return status === 'active' || pkg.isActive === true;
      });
    }
    
    return false;
  };

  // Check if user has active package
  const checkUserPackageStatus = async () => {
    try {
      if (!userDbKey || !registrationDate) return;
      
      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const isActive = hasActivePackages(userData);
        
        if (isActive) {
          setUserPackageStatus('active');
          setUserActivePackage(userData.packages?.find(pkg => 
            pkg.status === 'active' || pkg.packageStatus === 'active'
          ));
        } else {
          setUserPackageStatus('inactive');
          setUserActivePackage(null);
          setTeamPackages([]);
          setLevelSales(Array(11).fill({ sales: 0, members: 0 }));
        }
      }
    } catch (error) {
      console.error('Error checking user package status:', error);
      setUserPackageStatus('inactive');
    }
  };

  // Get user activation date (earliest active package)
  const getUserActivationDate = (user) => {
    if (!user || !user.packages) return null;
    
    const packages = user.packages || [];
    let earliestDate = null;
    
    if (Array.isArray(packages)) {
      packages.forEach(pkg => {
        if (pkg.status === 'active' || pkg.packageStatus === 'active') {
          const purchaseDate = new Date(pkg.purchaseDate || pkg.activationDate || pkg.date);
          if (!isNaN(purchaseDate.getTime())) {
            if (!earliestDate || purchaseDate < earliestDate) {
              earliestDate = purchaseDate;
            }
          }
        }
      });
    }
    
    return earliestDate;
  };

  // Calculate team sales
  const calculateTeamSales = async (sponsorId) => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (!snapshot.exists()) {
        setTeamPackages([]);
        setLevelSales(Array(11).fill({ sales: 0, members: 0 }));
        return;
      }

      const usersByDate = snapshot.val();
      const allPackages = [];
      const levelData = Array(11).fill().map(() => ({ sales: 0, members: new Set() }));
      
      // Get current user
      let currentUser = null;
      for (const date in usersByDate) {
        for (const [dbKey, user] of Object.entries(usersByDate[date])) {
          if (user.sponsorId === userSponsorId) {
            currentUser = user;
            break;
          }
        }
        if (currentUser) break;
      }
      
      if (!currentUser) {
        setTeamPackages([]);
        setLevelSales(levelData.map(ld => ({ sales: ld.sales, members: 0 })));
        return;
      }
      
      // Check if current user is active
      if (!hasActivePackages(currentUser)) {
        setTeamPackages([]);
        setLevelSales(levelData.map(ld => ({ sales: ld.sales, members: 0 })));
        return;
      }
      
      const uplineActivationDate = getUserActivationDate(currentUser);
      
      // Recursive function to find downlines
      const findDownlinePackages = (currentSponsorId, level = 1) => {
        if (level > 10) return;
        
        for (const date in usersByDate) {
          const users = usersByDate[date];
          
          for (const [dbKey, user] of Object.entries(users)) {
            if (user.uplineSponsorId === currentSponsorId) {
              
              // Check if downline is active
              const isDownlineActive = hasActivePackages(user);
              
              if (isDownlineActive) {
                levelData[level].members.add(user.sponsorId);
                
                // Extract packages from downline
                const packages = user.packages || [];
                
                if (Array.isArray(packages)) {
                  packages.forEach(pkg => {
                    const status = pkg.status || pkg.packageStatus;
                    if (status === 'active') {
                      const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.baseAmount) || 0;
                      const purchaseDate = new Date(pkg.purchaseDate || pkg.activationDate);
                      
                      if (!isNaN(purchaseDate.getTime())) {
                        // Check if purchased after upline activation
                        if (!uplineActivationDate || purchaseDate > uplineActivationDate) {
                          allPackages.push({
                            memberId: user.sponsorId,
                            memberName: user.name || 'Unknown',
                            packageName: pkg.packageName || 'Package',
                            totalAmount: amount,
                            level: level,
                            purchaseDate: purchaseDate.toISOString()
                          });
                          
                          levelData[level].sales += amount;
                        }
                      }
                    }
                  });
                }
              }
              
              // Recursively check next level
              findDownlinePackages(user.sponsorId, level + 1);
            }
          }
        }
      };

      findDownlinePackages(sponsorId, 1);
      
      // Format level data
      const formattedLevelSales = levelData.map((data, index) => ({
        sales: data.sales,
        members: data.members.size,
        level: index
      }));
      
      setTeamPackages(allPackages);
      setLevelSales(formattedLevelSales);
      
    } catch (error) {
      console.error('Error calculating leader rewards:', error);
      setTeamPackages([]);
      setLevelSales(Array(11).fill({ sales: 0, members: 0 }));
    }
  };

  const fetchAllRewards = async () => {
    try {
      const rewardsRef = ref(database, 'rewardSettings');
      const snapshot = await get(rewardsRef);
      
      const allRewardsList = [];
      
      if (snapshot.exists()) {
        const rewardsData = snapshot.val();
        
        // Process all timeframes
        Object.entries(rewardsData).forEach(([timeframe, rewardsArray]) => {
          if (timeframe !== 'teamsales' && Array.isArray(rewardsArray)) {
            rewardsArray.forEach((reward, index) => {
              if (reward && reward.status !== 'inactive') {
                allRewardsList.push({
                  ...reward,
                  id: reward.id || `${timeframe}_${index}`,
                  timeframe: timeframe,
                  order: index,
                  salesTarget: reward.requirement?.salesTarget || 0,
                  cashAmount: reward.reward?.amount || 0
                });
              }
            });
          }
        });
        
        // Sort by timeframe and order
        allRewardsList.sort((a, b) => {
          const timeframeOrder = { weekly: 1, biweekly: 2, monthly: 3 };
          if (a.timeframe !== b.timeframe) {
            return (timeframeOrder[a.timeframe] || 4) - (timeframeOrder[b.timeframe] || 4);
          }
          return (a.order || 0) - (b.order || 0);
        });
        
        setAllRewards(allRewardsList);
      } else {
        setAllRewards([]);
      }
    } catch (error) {
      console.error('Error fetching rewards:', error);
      setAllRewards([]);
    }
  };

  const fetchClaimedRewards = async () => {
    try {
      if (!userDbKey || !registrationDate) return;
      
      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      const claimedIds = new Set();
      let totalEarned = 0;
      
      if (snapshot.exists()) {
        const user = snapshot.val();
        
        if (user.leaderRewards && Array.isArray(user.leaderRewards)) {
          user.leaderRewards.forEach(reward => {
            if (reward.rewardId && reward.status === 'claimed') {
              claimedIds.add(reward.rewardId);
              totalEarned += parseFloat(reward.cashAmount) || 0;
            }
          });
        }
      }
      
      setClaimedRewardIds(claimedIds);
      setTotalRewardsEarned(totalEarned);
    } catch (error) {
      console.error('Error fetching claimed rewards:', error);
    }
  };

  // Calculate total team sales
  const calculateTotalTeamSales = () => {
    return teamPackages.reduce((sum, pkg) => sum + (parseFloat(pkg.totalAmount) || 0), 0);
  };

// FIXED: ALL TIMEFRAMES are sequential - same logic for Weekly, 14-Day, and Monthly
const calculateRewardProgress = (timeframe) => {
  if (userPackageStatus !== 'active') return [];
  
  const timeframeRewards = allRewards
    .filter(reward => reward.timeframe === timeframe)
    .sort((a, b) => a.order - b.order);
  
  if (timeframeRewards.length === 0) return [];
  
  const totalTeamSales = calculateTotalTeamSales();
  
  // Track claimed targets for THIS timeframe only
  let claimedTargetsForThisTimeframe = 0;
  timeframeRewards.forEach(reward => {
    if (claimedRewardIds.has(reward.id)) {
      claimedTargetsForThisTimeframe += reward.salesTarget || 0;
    }
  });
  
  let previousClaimed = true;
  
  return timeframeRewards.map((reward, index) => {
    const target = reward.salesTarget || 0;
    const isClaimed = claimedRewardIds.has(reward.id);
    
    let currentSales = 0;
    let progress = 0;
    let status = 'locked';
    let lockReason = null;
    
    if (isClaimed) {
      currentSales = target;
      progress = 100;
      status = 'claimed';
      previousClaimed = true;
    } 
    else if (previousClaimed) {
      // Check if previous reward is claimed
      const previousReward = index > 0 ? timeframeRewards[index - 1] : null;
      const isPreviousClaimed = previousReward ? claimedRewardIds.has(previousReward.id) : true;
      
      if (isPreviousClaimed) {
        // Previous reward is claimed - count sales minus claimed targets
        const availableSales = Math.max(0, totalTeamSales - claimedTargetsForThisTimeframe);
        currentSales = Math.min(availableSales, target);
        progress = target > 0 ? (currentSales / target) * 100 : 0;
        
        // Check if eligible (need enough TOTAL sales for cumulative target)
        const cumulativeTargetNeeded = claimedTargetsForThisTimeframe + target;
        if (totalTeamSales >= cumulativeTargetNeeded && currentSales >= target) {
          status = 'eligible';
        } else {
          status = 'in_progress';
        }
        
        previousClaimed = status === 'eligible';
      } else {
        // Previous reward NOT claimed - this reward is locked
        status = 'locked';
        lockReason = `Complete ${getTimeframeLabel(timeframe)} Reward ${index} first`;
        currentSales = 0;
        progress = 0;
        previousClaimed = false;
      }
    } 
    else {
      status = 'locked';
      lockReason = `Complete ${getTimeframeLabel(timeframe)} Reward ${index} first`;
    }
    
    return {
      ...reward,
      currentSales,
      progress: Math.min(100, progress),
      status,
      isEligible: status === 'eligible',
      isClaimed,
      lockReason,
      index: index + 1
    };
  });
};

  // Get all rewards with proper per-timeframe sequential logic
  const getAllRewardsWithProgress = () => {
    const weeklyRewards = calculateRewardProgress('weekly');
    const biweeklyRewards = calculateRewardProgress('biweekly');
    const monthlyRewards = calculateRewardProgress('monthly');
    
    return [...weeklyRewards, ...biweeklyRewards, ...monthlyRewards];
  };

  const getFilteredRewards = () => {
    if (userPackageStatus !== 'active') {
      return [];
    }
    
    const allProgressRewards = getAllRewardsWithProgress();
    
    // Filter by timeframe
    let filtered = allProgressRewards;
    if (timeFrame !== 'all') {
      filtered = filtered.filter(reward => reward.timeframe === timeFrame);
    }
    
    // Filter by status
    if (activeTab === 'available') {
      filtered = filtered.filter(reward => reward.status === 'eligible');
    } else if (activeTab === 'claimed') {
      filtered = filtered.filter(reward => reward.status === 'claimed');
    } else if (activeTab === 'locked') {
      filtered = filtered.filter(reward => reward.status === 'locked');
    }
    
    // Sort by timeframe and order
    filtered.sort((a, b) => {
      const timeframeOrder = { weekly: 1, biweekly: 2, monthly: 3 };
      if (a.timeframe !== b.timeframe) {
        return (timeframeOrder[a.timeframe] || 4) - (timeframeOrder[b.timeframe] || 4);
      }
      return (a.order || 0) - (b.order || 0);
    });
    
    return filtered;
  };

  const claimReward = async (reward) => {
    try {
      if (!userDbKey || !registrationDate) {
        showDialog('Session Error', 'Please login again', 'error');
        return;
      }

      if (claimingRewardId === reward.id) return;
      
      setClaimingRewardId(reward.id);
      
      if (!reward.isEligible) {
        const needed = (reward.salesTarget || 0) - reward.currentSales;
        showDialog('Not Ready', `You need $${needed.toFixed(2)} more in team sales to claim this reward.`, 'info');
        setClaimingRewardId(null);
        return;
      }
      
      const timestamp = new Date().toISOString();
      const cashAmount = reward.cashAmount || 0;
      const totalTeamSales = calculateTotalTeamSales();
      
      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      if (!snapshot.exists()) {
        showDialog('Error', 'User data not found', 'error');
        setClaimingRewardId(null);
        return;
      }
      
      const userData = snapshot.val();
      const currentWorkingBalance = parseFloat(userData.wallets?.working) || 0;
      const newWorkingBalance = currentWorkingBalance + cashAmount;
      
      const updates = {
        'wallets/working': newWorkingBalance,
        totalEarnings: (parseFloat(userData.totalEarnings) || 0) + cashAmount,
        updatedAt: timestamp
      };
      
      const claimedReward = {
        rewardId: reward.id,
        rewardName: reward.name,
        cashAmount: cashAmount,
        timeframe: reward.timeframe,
        salesTarget: reward.salesTarget || 0,
        teamSales: totalTeamSales,
        claimedAt: timestamp,
        status: 'claimed'
      };
      
      const currentLeaderRewards = userData.leaderRewards || [];
      updates['leaderRewards'] = [...currentLeaderRewards, claimedReward];
      
      await update(userRef, updates);
      
      // Update local state
      setClaimedRewardIds(prev => new Set([...prev, reward.id]));
      setTotalRewardsEarned(prev => prev + cashAmount);
      
      // Refresh data
      fetchUserData();
      fetchClaimedRewards();
      
      showDialog('Success', `Reward claimed! $${cashAmount.toFixed(2)} added to your Working Wallet.`, 'success');
      
    } catch (error) {
      console.error('Error claiming reward:', error);
      showDialog('Error', 'Failed to claim reward. Please try again.', 'error');
    } finally {
      setClaimingRewardId(null);
    }
  };

  const getTimeframeLabel = (timeframe) => {
    switch(timeframe) {
      case 'weekly': return 'Weekly';
      case 'biweekly': return '14-Day';
      case 'monthly': return 'Monthly';
      default: return timeframe;
    }
  };

  const formatNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return '0';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDecimal = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'claimed': return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' };
      case 'eligible': return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
      case 'in_progress': return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' };
      case 'locked': return { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' };
      default: return { bg: 'bg-secondary', text: 'text-muted-foreground', border: 'border-border' };
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 pt-20 pb-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const filteredRewards = getFilteredRewards();
  const totalTeamSales = calculateTotalTeamSales();
  const teamMembers = Array.from(new Set(teamPackages.map(pkg => pkg.memberId))).length;

  // Group rewards by timeframe for display
  const weeklyRewards = calculateRewardProgress('weekly');
  const biweeklyRewards = calculateRewardProgress('biweekly');
  const monthlyRewards = calculateRewardProgress('monthly');

  return (
    <>
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <div className="max-w-7xl mx-auto px-4 pt-20 pb-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Leader Rewards</h1>
          <p className="text-muted-foreground">
            {userPackageStatus === 'active' 
              ? `Earn from your team's sales (Levels 1-10)` 
              : 'Activate your package to start earning rewards'}
          </p>
        </div>

        {/* User Status Warning - Show when inactive */}
        {userPackageStatus !== 'active' && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-red-400 mt-1" size={20} />
              <div>
                <h3 className="font-bold text-red-400 mb-1">❌ Inactive - No Rewards</h3>
                <p className="text-red-300 text-sm">
                  You need an active package to view and claim leader rewards.
                </p>
                <div className="mt-2 text-xs text-red-400">
                  <strong>⚠️ IMPORTANT:</strong> Only sales made AFTER your activation date are eligible.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card-glow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Team Sales</p>
                <p className="text-xl font-bold text-foreground">
                  ${userPackageStatus === 'active' ? formatDecimal(totalTeamSales) : '0.00'}
                </p>
              </div>
              <DollarSign className={userPackageStatus === 'active' ? 'text-blue-400' : 'text-gray-400'} size={20} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">After activation</p>
          </div>

          <div className="card-glow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Active Members</p>
                <p className="text-xl font-bold text-foreground">
                  {userPackageStatus === 'active' ? teamMembers : 0}
                </p>
              </div>
              <Users className={userPackageStatus === 'active' ? 'text-green-400' : 'text-gray-400'} size={20} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Levels 1-10</p>
          </div>

          <div className="card-glow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Rewards Earned</p>
                <p className="text-xl font-bold text-foreground">
                  ${userPackageStatus === 'active' ? formatDecimal(totalRewardsEarned) : '0.00'}
                </p>
              </div>
              <Trophy className={userPackageStatus === 'active' ? 'text-yellow-400' : 'text-gray-400'} size={20} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Total claimed</p>
          </div>

          <div className="card-glow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Available Now</p>
                <p className="text-xl font-bold text-foreground">
                  {userPackageStatus === 'active' ? 
                    weeklyRewards.filter(r => r.status === 'eligible').length +
                    biweeklyRewards.filter(r => r.status === 'eligible').length +
                    monthlyRewards.filter(r => r.status === 'eligible').length
                  : 0}
                </p>
              </div>
              <Gift className={userPackageStatus === 'active' ? 'text-purple-400' : 'text-gray-400'} size={20} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">Ready to claim</p>
          </div>
        </div>

        {/* Level Breakdown Toggle */}
        {userPackageStatus === 'active' && (
          <div className="mb-6">
            <button
              onClick={() => setShowLevelBreakdown(!showLevelBreakdown)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition"
            >
              <Layers size={16} />
              {showLevelBreakdown ? 'Hide Level Breakdown' : 'Show Level Breakdown'}
            </button>
          </div>
        )}

        {/* Level Breakdown */}
        {showLevelBreakdown && userPackageStatus === 'active' && (
          <div className="mb-6 card-glow p-4">
            <h3 className="text-sm font-bold text-foreground mb-3">Sales by Level</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {levelSales.slice(1, 6).map((levelData, index) => (
                <div key={index + 1} className="text-center p-3 bg-secondary/50 rounded-lg">
                  <div className="text-lg font-bold text-primary">L{index + 1}</div>
                  <div className="text-sm font-medium text-foreground">
                    ${formatDecimal(levelData.sales)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {levelData.members} members
                  </div>
                </div>
              ))}
            </div>
            {levelSales.slice(6, 11).some(ld => ld.sales > 0 || ld.members > 0) && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Levels 6-10</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {levelSales.slice(6, 11).map((levelData, index) => (
                    <div key={index + 6} className="text-center p-2 bg-secondary/30 rounded">
                      <div className="text-sm font-medium text-primary">L{index + 6}</div>
                      <div className="text-xs text-foreground">
                        ${formatDecimal(levelData.sales)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Timeframe Tabs - Only show when active */}
        {userPackageStatus === 'active' && (
          <>
            <div className="mb-6">
              <div className="flex overflow-x-auto space-x-2 pb-2">
                <button
                  onClick={() => setTimeFrame('all')}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm ${
                    timeFrame === 'all' ? 'btn-electric' : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  All Rewards
                </button>
                <button
                  onClick={() => setTimeFrame('weekly')}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm ${
                    timeFrame === 'weekly' ? 'btn-electric' : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  Weekly
                </button>
                <button
                  onClick={() => setTimeFrame('biweekly')}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm ${
                    timeFrame === 'biweekly' ? 'btn-electric' : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  14-Day
                </button>
                <button
                  onClick={() => setTimeFrame('monthly')}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm ${
                    timeFrame === 'monthly' ? 'btn-electric' : 'bg-secondary text-muted-foreground'
                  }`}
                >
                  Monthly
                </button>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="mb-6">
              <div className="flex border-b border-border">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`flex-1 py-3 text-center text-sm ${
                    activeTab === 'all' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
                  }`}
                >
                  All ({allRewards.length})
                </button>
                <button
                  onClick={() => setActiveTab('available')}
                  className={`flex-1 py-3 text-center text-sm ${
                    activeTab === 'available' ? 'border-b-2 border-green-500 text-green-500' : 'text-muted-foreground'
                  }`}
                >
                  Available ({
                    weeklyRewards.filter(r => r.status === 'eligible').length +
                    biweeklyRewards.filter(r => r.status === 'eligible').length +
                    monthlyRewards.filter(r => r.status === 'eligible').length
                  })
                </button>
                <button
                  onClick={() => setActiveTab('claimed')}
                  className={`flex-1 py-3 text-center text-sm ${
                    activeTab === 'claimed' ? 'border-b-2 border-yellow-500 text-yellow-500' : 'text-muted-foreground'
                  }`}
                >
                  Claimed ({claimedRewardIds.size})
                </button>
              </div>
            </div>
          </>
        )}

        {/* Rewards List - SEQUENTIAL PER TIMEFRAME */}
        {userPackageStatus === 'active' ? (
          <div className="space-y-8">
            {/* Weekly Rewards Section */}
            {weeklyRewards.length > 0 && (timeFrame === 'all' || timeFrame === 'weekly') && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Calendar size={16} className="text-blue-400" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">Weekly Rewards</h2>
                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full">
                    {weeklyRewards.filter(r => r.status === 'claimed').length} / {weeklyRewards.length} Completed
                  </span>
                </div>
                
                <div className="relative">
                  {weeklyRewards.map((reward, index) => {
                    const colors = getStatusColor(reward.status);
                    const isLast = index === weeklyRewards.length - 1;
                    
                    return (
                      <div key={reward.id} className="relative">
                        {!isLast && (
                          <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-border z-0"></div>
                        )}
                        
                        <div className={`relative z-10 ml-6 card-glow p-4 border ${colors.border} ${colors.bg} mb-4`}>
                          <div className={`absolute -left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center border-2 ${colors.border} ${colors.bg}`}>
                            {reward.status === 'claimed' ? (
                              <CheckCircle size={16} className="text-green-400" />
                            ) : reward.status === 'locked' ? (
                              <Target size={16} className="text-gray-400" />
                            ) : (
                              <span className={`font-bold ${colors.text}`}>{index + 1}</span>
                            )}
                          </div>
                          
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-bold text-foreground">{reward.name}</h3>
                                <span className={`text-xs px-2 py-1 rounded-full ${colors.text} ${colors.border} border`}>
                                  {reward.status === 'claimed' ? '✅ Claimed' : 
                                   reward.status === 'eligible' ? '🎁 Ready' : 
                                   reward.status === 'locked' ? '🔒 Locked' : '⏳ Progress'}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400">
                                  Weekly
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                                  Target: ${formatNumber(reward.salesTarget)}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400">
                                  {reward.requirement?.periodDays || 7} days
                                </span>
                                {reward.status === 'locked' && index > 0 && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400">
                                    Complete Reward {index} first
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-400">
                                ${formatNumber(reward.cashAmount)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Reward
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="flex justify-between text-sm text-muted-foreground mb-1">
                              <span>Progress: {Math.round(reward.progress)}%</span>
                              <span>
                                ${formatDecimal(reward.currentSales)} / ${formatNumber(reward.salesTarget)}
                              </span>
                            </div>
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  reward.status === 'claimed' ? 'bg-green-500' :
                                  reward.status === 'eligible' ? 'bg-yellow-500' :
                                  reward.status === 'in_progress' ? 'bg-blue-500' :
                                  'bg-gray-500'
                                }`}
                                style={{ width: `${reward.progress}%` }}
                              ></div>
                            </div>
                          </div>

                          {reward.status === 'eligible' && !reward.isClaimed && (
                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={() => claimReward(reward)}
                                disabled={claimingRewardId === reward.id}
                                className="btn-electric px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-70"
                              >
                                {claimingRewardId === reward.id ? (
                                  <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                    Claiming...
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Gift size={16} />
                                    Claim ${formatNumber(reward.cashAmount)}
                                  </div>
                                )}
                              </button>
                            </div>
                          )}
                          
                          {reward.status === 'locked' && index > 0 && (
                            <div className="mt-3 p-3 bg-gray-500/10 rounded-lg">
                              <div className="flex items-center gap-2 text-gray-400">
                                <Target size={16} />
                                <span className="text-sm">
                                  Complete <span className="font-bold">Weekly Reward {index}</span> first
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 14-Day Rewards Section */}
            {biweeklyRewards.length > 0 && (timeFrame === 'all' || timeFrame === 'biweekly') && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Clock size={16} className="text-green-400" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">14-Day Rewards</h2>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded-full">
                    {biweeklyRewards.filter(r => r.status === 'claimed').length} / {biweeklyRewards.length} Completed
                  </span>
                </div>
                
                <div className="relative">
                  {biweeklyRewards.map((reward, index) => {
                    const colors = getStatusColor(reward.status);
                    const isLast = index === biweeklyRewards.length - 1;
                    
                    return (
                      <div key={reward.id} className="relative">
                        {!isLast && (
                          <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-border z-0"></div>
                        )}
                        
                        <div className={`relative z-10 ml-6 card-glow p-4 border ${colors.border} ${colors.bg} mb-4`}>
                          <div className={`absolute -left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center border-2 ${colors.border} ${colors.bg}`}>
                            {reward.status === 'claimed' ? (
                              <CheckCircle size={16} className="text-green-400" />
                            ) : reward.status === 'locked' ? (
                              <Target size={16} className="text-gray-400" />
                            ) : (
                              <span className={`font-bold ${colors.text}`}>{index + 1}</span>
                            )}
                          </div>
                          
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-bold text-foreground">{reward.name}</h3>
                                <span className={`text-xs px-2 py-1 rounded-full ${colors.text} ${colors.border} border`}>
                                  {reward.status === 'claimed' ? '✅ Claimed' : 
                                   reward.status === 'eligible' ? '🎁 Ready' : 
                                   reward.status === 'locked' ? '🔒 Locked' : '⏳ Progress'}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-green-500/20 text-green-400">
                                  14-Day
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                                  Target: ${formatNumber(reward.salesTarget)}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400">
                                  {reward.requirement?.periodDays || 14} days
                                </span>
                                {reward.status === 'locked' && index > 0 && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400">
                                    Complete Reward {index} first
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-400">
                                ${formatNumber(reward.cashAmount)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Reward
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="flex justify-between text-sm text-muted-foreground mb-1">
                              <span>Progress: {Math.round(reward.progress)}%</span>
                              <span>
                                ${formatDecimal(reward.currentSales)} / ${formatNumber(reward.salesTarget)}
                              </span>
                            </div>
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  reward.status === 'claimed' ? 'bg-green-500' :
                                  reward.status === 'eligible' ? 'bg-yellow-500' :
                                  reward.status === 'in_progress' ? 'bg-blue-500' :
                                  'bg-gray-500'
                                }`}
                                style={{ width: `${reward.progress}%` }}
                              ></div>
                            </div>
                          </div>

                          {reward.status === 'eligible' && !reward.isClaimed && (
                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={() => claimReward(reward)}
                                disabled={claimingRewardId === reward.id}
                                className="btn-electric px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-70"
                              >
                                {claimingRewardId === reward.id ? (
                                  <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                    Claiming...
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Gift size={16} />
                                    Claim ${formatNumber(reward.cashAmount)}
                                  </div>
                                )}
                              </button>
                            </div>
                          )}
                          
                          {reward.status === 'locked' && index > 0 && (
                            <div className="mt-3 p-3 bg-gray-500/10 rounded-lg">
                              <div className="flex items-center gap-2 text-gray-400">
                                <Target size={16} />
                                <span className="text-sm">
                                  Complete <span className="font-bold">14-Day Reward {index}</span> first
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Monthly Rewards Section */}
            {monthlyRewards.length > 0 && (timeFrame === 'all' || timeFrame === 'monthly') && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <Calendar size={16} className="text-purple-400" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">Monthly Rewards</h2>
                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded-full">
                    {monthlyRewards.filter(r => r.status === 'claimed').length} / {monthlyRewards.length} Completed
                  </span>
                </div>
                
                <div className="relative">
                  {monthlyRewards.map((reward, index) => {
                    const colors = getStatusColor(reward.status);
                    const isLast = index === monthlyRewards.length - 1;
                    
                    return (
                      <div key={reward.id} className="relative">
                        {!isLast && (
                          <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-border z-0"></div>
                        )}
                        
                        <div className={`relative z-10 ml-6 card-glow p-4 border ${colors.border} ${colors.bg} mb-4`}>
                          <div className={`absolute -left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center border-2 ${colors.border} ${colors.bg}`}>
                            {reward.status === 'claimed' ? (
                              <CheckCircle size={16} className="text-green-400" />
                            ) : reward.status === 'locked' ? (
                              <Target size={16} className="text-gray-400" />
                            ) : (
                              <span className={`font-bold ${colors.text}`}>{index + 1}</span>
                            )}
                          </div>
                          
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-bold text-foreground">{reward.name}</h3>
                                <span className={`text-xs px-2 py-1 rounded-full ${colors.text} ${colors.border} border`}>
                                  {reward.status === 'claimed' ? '✅ Claimed' : 
                                   reward.status === 'eligible' ? '🎁 Ready' : 
                                   reward.status === 'locked' ? '🔒 Locked' : '⏳ Progress'}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400">
                                  Monthly
                                </span>
                              </div>
                              
                              <div className="flex flex-wrap gap-2 mb-3">
                                <span className="text-xs px-2 py-1 rounded-full bg-secondary text-muted-foreground">
                                  Target: ${formatNumber(reward.salesTarget)}
                                </span>
                                <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400">
                                  {reward.requirement?.periodDays || 30} days
                                </span>
                                {reward.status === 'locked' && index > 0 && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400">
                                    Complete Reward {index} first
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <div className="text-lg font-bold text-green-400">
                                ${formatNumber(reward.cashAmount)}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                Reward
                              </div>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="flex justify-between text-sm text-muted-foreground mb-1">
                              <span>Progress: {Math.round(reward.progress)}%</span>
                              <span>
                                ${formatDecimal(reward.currentSales)} / ${formatNumber(reward.salesTarget)}
                              </span>
                            </div>
                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  reward.status === 'claimed' ? 'bg-green-500' :
                                  reward.status === 'eligible' ? 'bg-yellow-500' :
                                  reward.status === 'in_progress' ? 'bg-blue-500' :
                                  'bg-gray-500'
                                }`}
                                style={{ width: `${reward.progress}%` }}
                              ></div>
                            </div>
                          </div>

                          {reward.status === 'eligible' && !reward.isClaimed && (
                            <div className="mt-4 flex justify-end">
                              <button
                                onClick={() => claimReward(reward)}
                                disabled={claimingRewardId === reward.id}
                                className="btn-electric px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-70"
                              >
                                {claimingRewardId === reward.id ? (
                                  <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                    Claiming...
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Gift size={16} />
                                    Claim ${formatNumber(reward.cashAmount)}
                                  </div>
                                )}
                              </button>
                            </div>
                          )}
                          
                          {reward.status === 'locked' && index > 0 && (
                            <div className="mt-3 p-3 bg-gray-500/10 rounded-lg">
                              <div className="flex items-center gap-2 text-gray-400">
                                <Target size={16} />
                                <span className="text-sm">
                                  Complete <span className="font-bold">Monthly Reward {index}</span> first
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {weeklyRewards.length === 0 && biweeklyRewards.length === 0 && monthlyRewards.length === 0 && (
              <div className="card-glow p-8 text-center">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-bold text-foreground mb-2">No Rewards Found</h3>
                <p className="text-muted-foreground">
                  No leader rewards configured yet.
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Show empty state when user is inactive - NO REWARDS VISIBLE */
          <div className="card-glow p-8 text-center">
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">Inactive - No Rewards</h3>
            <p className="text-muted-foreground mb-6">
              You need an active package to view and claim leader rewards.
            </p>
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 max-w-md mx-auto">
              <p className="text-sm text-blue-400">
                Activate a package to begin earning rewards from your team!
              </p>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default LeaderRewards;
